import { Button, Grid, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import * as yup from 'yup';
import { Schema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGlobals } from 'src/shared/contexts/appContext';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import FileDropZone from 'src/shared/components/form/fileDropZone';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useCallback, useState } from 'react';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { createDocument } from 'src/shared/services/documentService';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { getFileAsBase64WithoutPrefix } from 'src/shared/functions/fileExtensions';
import { toast } from 'react-toastify';
import { MimeTypeEnum } from 'src/shared/models/enums/mimeType.enum';
import { DocumentCategory } from '../models/documentCategory';
import { DocumentRequest } from '../models/documentRequest';

type UploadDocumentFileProps = {
  openModal: boolean;
  setOpenModal: (openModal: boolean) => void;
  documentCategories: DocumentCategory[];
  refetchDataGrid: (refetch: boolean) => void;
};

export const UploadDocumentFile = (props: UploadDocumentFileProps) => {
  const { openModal, setOpenModal, documentCategories, refetchDataGrid } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [loading, setLoading] = useState<boolean>(false);

  const schema: Schema<DocumentRequest> = yup.object().shape({
    documentCategory_id: yup.number().required(t('common:validationMessages.required')),
    user_id: yup.string().required(t('common:validationMessages.required')),
    label: yup.string().required(t('common:validationMessages.required')),
    fileName: yup.string().required(t('common:validationMessages.required')),
    description: yup.string().required(t('common:validationMessages.required')),
    // metaWords: yup.string().required(t('common:validationMessages.required')),
    documentData: yup.string().required(t('common:validationMessages.required')),
    // filesize: yup.string().required(t('common:validationMessages.required')),
    contentType: yup.string<MimeTypeEnum>().required(t('common:validationMessages.required')),
    private: yup.boolean().required(t('common:validationMessages.required')),
    // fileSize: yup.string().required(),
    // guid: yup.string().required(t('common:validationMessages.required')),
  });

  const formMethods = useForm<DocumentRequest>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      documentCategory_id: 0,
      label: '',
      user_id: currentUser?.userName,
      fileName: '',
      description: '',
      //   metaWords: '',
      documentData: '',
      //   fileSize: '',
      contentType: '',
      private: true,
    },
  });

  const { handleSubmit, reset, setValue } = formMethods;

  const onHandleClose = () => {
    reset();
    setOpenModal(false);
  };

  const handleFilesChange = useCallback(
    async (files: File[]) => {
      const file = files?.[0];
      if (isMounted()) {
        if (!file) {
          setValue('documentData', '');
          setValue('contentType', '');
        } else {
          const base64 = await getFileAsBase64WithoutPrefix(file);
          setValue('fileName', file.name, { shouldValidate: true });
          setValue('documentData', base64);
          setValue('contentType', file.type);
          // setValue('fileSize', file.size.toString());
        }
      }
    },
    [isMounted, setValue],
  );

  const onSubmit = async (data: DocumentRequest) => {
    setLoading(true);

    await createDocument(data);
    try {
      if (isMounted()) {
        toast.success(<>{t('documentArchive:createdSuccess')}</>);
        refetchDataGrid(true);
        onHandleClose();
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  };

  const formId = 'create-file';
  return (
    <GenericDialog
      maxWidth='sm'
      disableBackdropClosing
      open={openModal}
      onClose={onHandleClose}
      onSave={() => handleSubmit(onSubmit)}
      dialogTitle={t('documentArchive:uploadFile')}
      dialogContent={
        <>
          <FormProvider {...formMethods}>
            <form id={formId} onSubmit={handleSubmit(onSubmit)}>
              <>
                <Grid container spacing={2}>
                  <LoadingOverlay loading={loading} message={t('documentArchive:uploadingFile')} />
                  <Grid item xs={6}>
                    <AutocompleteDropdown<DocumentCategory>
                      keyProp='documentCategory_id'
                      nameProp='category'
                      label={t('documentArchive:documentCategory.category')}
                      emptyLabel={documentCategories?.length === 0 ? t('documentArchive:documentCategory.noCategories') : t('common:button.choose')}
                      placeholder={t('common:button.choose')}
                      options={documentCategories || []}
                      callback={(selectedOption) => {
                        if (selectedOption) {
                          setValue('documentCategory_id', selectedOption.documentCategory_id || 0);
                        } else {
                          setValue('documentCategory_id', 0);
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <ControlledTextField name='label' label={t('documentArchive:documentTitle')} />
                  </Grid>

                  <Grid item xs={6}>
                    <ControlledTextField disabled label={t('documentArchive:fileName')} name='fileName' />
                  </Grid>

                  <Grid item xs={6}>
                    <ControlledTextField label={t('documentArchive:documentCategory.description')} name='description' />
                  </Grid>

                  <Grid item xs={6}>
                    <ControlledCheckboxNew
                      name='private'
                      customizedLabel={
                        <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('documentArchive:markAsPrivate')}
                        </Typography>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FileDropZone
                      orientation='vertical'
                      onFilesChange={handleFilesChange}
                      onDocumentFileRemove={() => {
                        setValue('fileName', '');
                        setValue('contentType', '');
                        setValue('documentData', '');
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            </form>
          </FormProvider>
        </>
      }
      dialogActions={
        <Grid container justifyContent='flex-end'>
          <Grid item>
            <Button form={formId} sx={{ marginLeft: 2 }} disabled={loading} disableElevation variant='contained' color='primary' type='submit'>
              {t('common:button.save')}
            </Button>
          </Grid>
        </Grid>
      }
    />
  );
};
